/**
 * Category Selector with Search & Request Feature
 *
 * Features:
 * - Searchable dropdown
 * - Request new category
 * - Modal for submission form
 * - Validation
 * - Success feedback
 */

import { ChevronDown, Plus, Search, AlertCircle, CheckCircle } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

import { CATEGORIES } from '@jemmi/schemas';
import { cn } from '@/lib/cn';

type Props = {
  value: string;
  onChange: (value: string) => void;
};

/**
 * Searchable category dropdown with ability to request new categories.
 * As categories grow, dropdown prevents long lists.
 */
export function CategorySelector({ value, onChange }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestCategory, setRequestCategory] = useState('');
  const [requestDescription, setRequestDescription] = useState('');
  const [requestError, setRequestError] = useState('');
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter categories based on search
  const filteredCategories = CATEGORIES.filter((cat) =>
    cat.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelectCategory = (category: string) => {
    onChange(category);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRequestError('');

    // Validate
    if (!requestCategory.trim()) {
      setRequestError('Category name is required');
      return;
    }
    if (requestCategory.length > 40) {
      setRequestError('Category name must be less than 40 characters');
      return;
    }
    if (CATEGORIES.some((c) => c === requestCategory)) {
      setRequestError('This category already exists');
      return;
    }

    setRequestLoading(true);

    try {
      // TODO: Phase 2 - POST /api/admin/category-requests
      console.log('[category-request] Submitting:', {
        categoryName: requestCategory,
        description: requestDescription,
      });

      await new Promise((r) => setTimeout(r, 1500));

      setRequestSubmitted(true);
      setRequestCategory('');
      setRequestDescription('');

      // Auto-close after 3 seconds
      setTimeout(() => {
        setShowRequestModal(false);
        setRequestSubmitted(false);
      }, 3000);
    } catch (error) {
      setRequestError('Failed to submit request. Please try again.');
    } finally {
      setRequestLoading(false);
    }
  };

  return (
    <>
      {/* Category Selector Dropdown */}
      <div ref={dropdownRef} className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'flex w-full items-center justify-between rounded-lg border-2 px-4 py-3 text-left text-sm font-medium transition-all',
            isOpen
              ? 'border-brand-500 bg-brand-50 text-brand-700'
              : 'border-slate-200 bg-white text-slate-900 hover:border-slate-300',
          )}
        >
          <span>{value}</span>
          <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
            {/* Search Input */}
            <div className="border-b border-slate-200 p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search categories..."
                  className="focus:ring-brand-200 w-full rounded-lg border border-slate-200 py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2"
                />
              </div>
            </div>

            {/* Category List */}
            <div className="max-h-48 overflow-y-auto">
              {filteredCategories.length > 0 ? (
                filteredCategories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => handleSelectCategory(category)}
                    className={cn(
                      'w-full border-b border-slate-100 px-4 py-2.5 text-left text-sm transition-colors last:border-b-0',
                      value === category
                        ? 'bg-brand-50 text-brand-700 font-semibold'
                        : 'text-slate-700 hover:bg-slate-50',
                    )}
                  >
                    {category}
                  </button>
                ))
              ) : (
                <div className="px-4 py-6 text-center text-sm text-slate-500">
                  No categories match your search
                </div>
              )}
            </div>

            {/* Request New Category Button */}
            <div className="border-t border-slate-200 p-3">
              <button
                type="button"
                onClick={() => {
                  setShowRequestModal(true);
                  setIsOpen(false);
                }}
                className="text-brand-600 hover:bg-brand-50 flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
              >
                <Plus className="h-4 w-4" />
                Request new category
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
            {/* Success State */}
            {requestSubmitted ? (
              <div className="space-y-4 p-6 text-center">
                <div className="flex justify-center">
                  <CheckCircle className="h-12 w-12 text-green-500" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Request submitted!</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    We'll review "{requestCategory}" and notify you once it's approved or denied.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleRequestSubmit} className="space-y-4 p-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Request a new category</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    Help us grow! Suggest a category you'd like to see.
                  </p>
                </div>

                {/* Category Name */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-900">
                    Category name
                  </label>
                  <input
                    type="text"
                    value={requestCategory}
                    onChange={(e) => setRequestCategory(e.target.value)}
                    placeholder="E.g., Fashion, Art, Sports"
                    maxLength={40}
                    className={cn(
                      'w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2',
                      requestError
                        ? 'border-red-300 focus:ring-red-200'
                        : 'focus:ring-brand-200 border-slate-200',
                    )}
                    disabled={requestLoading}
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    {requestCategory.length}/40 characters
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-900">
                    Why do you need this category?
                  </label>
                  <textarea
                    value={requestDescription}
                    onChange={(e) => setRequestDescription(e.target.value)}
                    placeholder="E.g., For photography challenges related to fashion photography"
                    maxLength={200}
                    rows={3}
                    className="focus:ring-brand-200 w-full resize-none rounded-lg border border-slate-200 px-4 py-2 text-sm focus:outline-none focus:ring-2"
                    disabled={requestLoading}
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    {requestDescription.length}/200 characters
                  </p>
                </div>

                {/* Error */}
                {requestError && (
                  <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
                    <p className="text-sm text-red-700">{requestError}</p>
                  </div>
                )}

                {/* Info */}
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <p className="text-xs text-blue-800">
                    Our team will review your request and notify you of the decision within 48
                    hours.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3 border-t border-slate-200 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowRequestModal(false)}
                    disabled={requestLoading}
                    className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 font-semibold text-slate-900 transition-all hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!requestCategory.trim() || requestLoading}
                    className={cn(
                      'flex-1 rounded-lg py-2.5 font-semibold text-white transition-all',
                      !requestCategory.trim() || requestLoading
                        ? 'cursor-not-allowed bg-slate-300'
                        : 'from-brand-500 to-brand-700 hover:shadow-brand-md bg-gradient-to-br active:scale-[0.98]',
                    )}
                  >
                    {requestLoading ? 'Submitting...' : 'Submit request'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
